import {
  AbsoluteFill,
  interpolate,
  OffthreadVideo,
  Sequence,
  staticFile,
  useCurrentFrame,
} from "remotion";
import { z } from "zod";
import { AnimatedTDCCard } from "./AnimatedTDCCard";
import { resolveBadge } from "./roles";
import { DEFAULT_TRILHA_KEY } from "../../lib/promo-form-config";
import { getEdition } from "../../lib/editions";
import {
  CARD_DURATION_FRAMES,
  CARD_EXIT_FRAME,
  ELEMENTS_IN_FRAME,
  FPS,
  PALESTRA_TYPE_KEYS,
  ROLE_KEYS,
} from "../../lib/types";

// Duotone overlay fade — ramps in from 2.8 s up to ELEMENTS_IN_FRAME
// (3 s) so the overlay reaches full opacity right as the card elements
// come in, instead of hard-cutting in.
const OVERLAY_FADE_START = Math.round(2.8 * FPS); // 2.8 s
const OVERLAY_FADE_END = ELEMENTS_IN_FRAME; // 3 s
const DUOTONE_MAX_OPACITY = 0.82;

export const tdcPromoVideoSchema = z.object({
  role: z.enum(ROLE_KEYS),
  palestraType: z.enum(PALESTRA_TYPE_KEYS).optional().default(DEFAULT_TRILHA_KEY),
  speakerName: z.string(),
  speakerTitle: z.string(),
  talkTitle: z.string(),
  photoUrl: z.string(),
  /** User-controlled photo translation, composition-space pixels. */
  photoOffsetX: z.number().optional().default(0),
  photoOffsetY: z.number().optional().default(0),
  /** User-controlled scale multiplier for the speaker photo. */
  photoScale: z.number().optional().default(1),
  speaker2Name: z.string().optional().default(""),
  speaker2Title: z.string().optional().default(""),
  photoUrl2: z.string().optional().default(""),
  photoOffsetX2: z.number().optional().default(0),
  photoOffsetY2: z.number().optional().default(0),
  photoScale2: z.number().optional().default(1),
  sharedPhoto: z.boolean().optional().default(false),
  editionSlug: z.string(),
  /**
   * When true, the composition overlays the social-media safe-area guide.
   * Should only be true inside the Remotion <Player> preview — the MP4
   * render must leave this OFF so the final video is clean.
   */
  showSafeArea: z.boolean().optional().default(false),
});

export type TDCPromoVideoProps = z.infer<typeof tdcPromoVideoSchema>;

export const TDCPromoVideo: React.FC<TDCPromoVideoProps> = (props) => {
  // The tint color follows the visible badge, whether that is a selected
  // trilha for Palestra or the Coordenação type.
  const tintColor = resolveBadge(props.role, props.palestraType).color;
  const backgroundSrc = getEdition(props.editionSlug).backgroundAsset;

  // Drive the overlay fade directly from the composition frame rather
  // than wrapping in a <Sequence>, so the ramp can start before the
  // rest of the elements come in.
  const frame = useCurrentFrame();
  const overlayOpacity = interpolate(frame, [OVERLAY_FADE_START, OVERLAY_FADE_END], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ backgroundColor: "black" }}>
      {/* ── Background video — source is already black & white, so no
            CSS filter needed. Plays the whole 15 s edge-to-edge with
            its native audio so the exported MP4 and the live Player
            preview both carry the TDC background soundtrack.
            Uses <OffthreadVideo> instead of <Video> so during render
            FFmpeg extracts the exact frame at each timestamp — the
            classic HTML5 seek snaps to GOP boundaries and was causing
            duplicated/reordered frames ("glitching") in the output. ── */}
      <OffthreadVideo
        src={staticFile(backgroundSrc)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />

      {/* ── Format-color duotone + vignette — ramps in from 2.5 s to
            3 s (full opacity at the ELEMENTS_IN_FRAME mark) so the cut
            from pristine B&W to tinted-duotone isn't jarring. Stays on
            for the rest of the composition. ── */}
      {/* Format-color duotone — painted on top of the grayscale video
          with mix-blend-mode: multiply so the video's luminance is
          preserved but the highlights/shadows pick up the tint. */}
      <AbsoluteFill
        style={{
          backgroundColor: tintColor,
          mixBlendMode: "multiply",
          opacity: DUOTONE_MAX_OPACITY * overlayOpacity,
        }}
      />
      {/* Subtle radial vignette darkening the corners for legibility. */}
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)",
          pointerEvents: "none",
          opacity: overlayOpacity,
        }}
      />

      {/* ── TDC card elements — visible from 3 s to 13 s, then fade ── */}
      <Sequence from={ELEMENTS_IN_FRAME} durationInFrames={CARD_DURATION_FRAMES}>
        <AnimatedTDCCard {...props} exitStartFrame={CARD_EXIT_FRAME} />
      </Sequence>
    </AbsoluteFill>
  );
};
